import { useAzureToken } from '@internal/backstage-plugin-kaap';
import {
  armApiRef,
  Group,
  useAzureOwnGroups,
  useSecurityGroupNames,
} from '@internal/plugin-azure-resources-common-react';
import { ScaffolderFieldError } from '@internal/plugin-scaffolder-common-field-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDebounce from 'react-use/lib/useDebounce';

import { useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { Chip, FormControl, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { wellKnownGroupPrefix } from './wellKnownGroups';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const isWellKnownGroup = (name: string): boolean => {
  const upperName = name.toUpperCase();
  return wellKnownGroupPrefix.some(prefix =>
    upperName.startsWith(prefix.toUpperCase()),
  );
};

export const AzureSecurityGroupMultiValue = (
  props: FieldExtensionComponentProps<string[]>,
) => {
  const {
    onChange,
    schema: { title = '', description = '' },
    required,
    rawErrors,
    formData,
    idSchema,
    uiSchema,
    maxTags = 5,
  } = props;

  const [isRequired, setSelectRequired] = useState<boolean>(required ?? false);
  const hasError = rawErrors.length > 0;

  const { excludeWellKnownGroups } = uiSchema?.['ui:options'] ?? {};

  const armApi = useApi(armApiRef);
  const { authProvider } = useAzureToken();
  const { value: ownGroups } = useAzureOwnGroups();
  const { groups: selectedNames } = useSecurityGroupNames(formData ?? []);

  useEffect(() => {
    if (Array.isArray(formData) && formData?.length > 0) {
      setSelectRequired(false);
    } else {
      setSelectRequired(required ?? false);
    }
  }, [required, setSelectRequired, formData?.length, formData]);

  const [names, setNames] = useState<Map<string, string>>(new Map());

  const mergeNames = useCallback(
    (entries: Iterable<[string, string | undefined]>) => {
      setNames(previous => {
        let next = previous;
        for (const [id, name] of entries) {
          if (!name || next.get(id) === name) continue;
          if (next === previous) next = new Map(previous);
          next.set(id, name);
        }
        return next;
      });
    },
    [],
  );

  const ownOptions = useMemo<Group[]>(
    () =>
      ownGroups.map(group => ({
        id: group.id,
        name: group.displayName ?? '',
      })),
    [ownGroups],
  );

  useEffect(() => {
    mergeNames(ownOptions.map(group => [group.id, group.name]));
  }, [ownOptions, mergeNames]);

  useEffect(() => {
    mergeNames(selectedNames);
  }, [selectedNames, mergeNames]);

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Group[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  const searchCache = useRef(new Map<string, Group[]>());

  const requestId = useRef(0);

  const query = term.trim();
  const isSearch = query.length >= MIN_SEARCH_LENGTH;

  useDebounce(
    () => {
      const id = ++requestId.current;

      if (!isSearch) {
        setResults([]);
        setSearchFailed(false);
        setSearchLoading(false);
        return;
      }

      // Only an exact match may be served from the cache
      const key = query.toLowerCase();
      const cached = searchCache.current.get(key);
      if (cached) {
        setResults(cached);
        setSearchFailed(false);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      armApi
        .getGroups(authProvider, { match: query })
        .then(found => {
          searchCache.current.set(key, found);
          mergeNames(found.map(group => [group.id, group.name]));
          if (id !== requestId.current) return;
          setResults(found);
          setSearchFailed(false);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setResults([]);
          setSearchFailed(true);
        })
        .finally(() => {
          if (id === requestId.current) setSearchLoading(false);
        });
    },
    SEARCH_DEBOUNCE_MS,
    [query, isSearch],
  );

  const options = useMemo(() => {
    // Search results already went through ArmApiClient's group filter, own groups did not.
    const source = isSearch
      ? results
      : ownOptions.filter(
          group => !excludeWellKnownGroups || !isWellKnownGroup(group.name),
        );

    return Array.from(
      new Set([...(formData ?? []), ...source.map(group => group.id)]),
    );
  }, [isSearch, results, ownOptions, excludeWellKnownGroups, formData]);

  const optionLabel = useCallback(
    (option: string) => names.get(option) || option,
    [names],
  );

  let helperMessage = description;
  if (searchFailed) {
    helperMessage =
      'Could not search Azure security groups. Check your Azure sign-in and try again.';
  } else if (isSearch && results.length > 0) {
    helperMessage =
      'Showing the closest matches. Type more of the name to narrow them down.';
  }

  return (
    <FormControl fullWidth required={required}>
      <Autocomplete
        id={idSchema?.$id}
        multiple
        size="small"
        value={formData || []}
        onChange={(_: any, value: string[] | null) => {
          onChange(value || []);
        }}
        onInputChange={(_, value) => setTerm(value)}
        options={options}
        getOptionLabel={optionLabel}
        renderOption={(option: string) => optionLabel(option)}
        limitTags={maxTags}
        loading={searchLoading}
        loadingText="Searching Azure security groups..."
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            size="small"
            required={isRequired}
            InputProps={params.InputProps}
            variant="outlined"
            color="secondary"
            fullWidth
            error={hasError && !formData?.length}
            helperText={
              hasError ? (
                <ScaffolderFieldError message={rawErrors.join(', ')} />
              ) : (
                <Typography
                  variant="caption"
                  color={searchFailed ? 'error' : 'textSecondary'}
                >
                  {helperMessage}
                </Typography>
              )
            }
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={optionLabel(option)}
              color="secondary"
              size="small"
            />
          ))
        }
      />
    </FormControl>
  );
};
